import { Fragment, useMemo, useRef } from "react";
import { FieldColumnName } from "../../fields/FieldColumnName";
import { FieldDefaultFormulaIdentifier } from "../../fields/FieldDefaultFormulaIdentifier";
import { FieldTable } from "../../fields/FieldTable";
import { PanelProps } from "../ModalCreateColumn";

export const PanelCreateColumnIdentifier = ({
  autoFocus = true,
  isDefinitionEditable = true,
  fields,
  onFieldChange,
}: PanelProps) => {
  const nameFieldRef = useRef<HTMLInputElement>(null);

  // Parse file type from column definition
  const table = useMemo(() => {
    return fields.definition.value?.kind === "identifier"
      ? fields.definition.value.table
      : undefined;
  }, [fields.definition.value]);

  /**
   * Shifts focus to the name field.
   *
   * This is tried multiple times because EuiSuperSelect (used by
   * FieldFileMetadatum) refocuses itself after onChange is called. I believe
   * this is a bug in EUI but I think this is the best option in the absence of
   * a long-term fix.
   */
  const focusNameField = () => {
    const timer = setInterval(() => {
      if (document.activeElement !== nameFieldRef.current) {
        nameFieldRef.current?.focus();
      } else {
        clearInterval(timer);
      }
    });
  };

  return (
    <Fragment>
      <FieldTable
        autoFocus={autoFocus}
        disabled={!isDefinitionEditable}
        value={table?.id}
        onChange={(value) => {
          // Update state
          if (value !== undefined) {
            onFieldChange("definition", {
              value: {
                kind: "identifier",
                table: value,
              },
              isValid: true,
            });
          } else {
            onFieldChange("definition", {
              value: undefined,
              isValid: false,
            });
          }

          // Reset default formula
          onFieldChange("default_formula", {
            value: undefined,
            isValid: true,
          });

          // Auto-fill name field if not previously edited
          if (value !== undefined && !fields.name.isDirty) {
            onFieldChange("name", {
              value: value.name,
              isValid: true,
              isDirty: false,
            });
          }

          // Shift focus to name field
          focusNameField();
        }}
      />

      <FieldColumnName
        ref={nameFieldRef}
        value={fields.name.value}
        onChange={(field) => {
          onFieldChange("name", {
            value: field.value,
            isValid: field.isValid,
            isDirty: true,
          });
        }}
      />

      <FieldDefaultFormulaIdentifier
        tableId={table?.id}
        value={fields.default_formula.value}
        onChange={(value) => {
          onFieldChange("default_formula", {
            value: value,
            isValid: true,
          });
        }}
      />
    </Fragment>
  );
};
